import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const fetchMatches = createAsyncThunk(
  'matches/fetchMatches',
  async ({ limit = 20, minScore = 50 } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(`/matches?limit=${limit}&min_score=${minScore}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchMatchStats = createAsyncThunk(
  'matches/fetchMatchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/matches/stats');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchCompatibility = createAsyncThunk(
  'matches/fetchCompatibility',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/matches/${userId}/compatibility`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchCompatibilityDetails = createAsyncThunk(
  'matches/fetchCompatibilityDetails',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/matches/${userId}/details`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

const initialState = {
  items: [],
  stats: null,
  compatibility: null,
  compatibilityDetails: null,
  loading: false,
  statsLoading: false,
  compatibilityLoading: false,
  error: null,
  statsError: null,
  compatibilityError: null,
};

const matchesSlice = createSlice({
  name: 'matches',
  initialState,
  reducers: {
    clearMatchesError: (state) => {
      state.error = null;
    },
    clearStatsError: (state) => {
      state.statsError = null;
    },
    clearCompatibilityError: (state) => {
      state.compatibilityError = null;
    },
    resetMatches: (state) => {
      state.items = [];
      state.stats = null;
      state.compatibility = null;
      state.compatibilityDetails = null;
    },
    updateMatch: (state, action) => {
      const index = state.items.findIndex(item => item.user.id === action.payload.user.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
    removeMatch: (state, action) => {
      state.items = state.items.filter(item => item.user.id !== action.payload.userId);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch matches
      .addCase(fetchMatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMatches.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchMatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch match stats
      .addCase(fetchMatchStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchMatchStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchMatchStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload;
      })

      // Fetch compatibility
      .addCase(fetchCompatibility.pending, (state) => {
        state.compatibilityLoading = true;
        state.compatibilityError = null;
      })
      .addCase(fetchCompatibility.fulfilled, (state, action) => {
        state.compatibilityLoading = false;
        state.compatibility = action.payload;
      })
      .addCase(fetchCompatibility.rejected, (state, action) => {
        state.compatibilityLoading = false;
        state.compatibilityError = action.payload;
      })

      // Fetch compatibility details
      .addCase(fetchCompatibilityDetails.pending, (state) => {
        state.compatibilityLoading = true;
        state.compatibilityError = null;
      })
      .addCase(fetchCompatibilityDetails.fulfilled, (state, action) => {
        state.compatibilityLoading = false;
        state.compatibilityDetails = action.payload;
      })
      .addCase(fetchCompatibilityDetails.rejected, (state, action) => {
        state.compatibilityLoading = false;
        state.compatibilityError = action.payload;
      });
  },
});

export const {
  clearMatchesError,
  clearStatsError,
  clearCompatibilityError,
  resetMatches,
  updateMatch,
  removeMatch
} = matchesSlice.actions;

export default matchesSlice.reducer;
